// biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: shared component anatomy.
// biome-ignore-all lint/a11y/useFocusableInteractive: shared component anatomy.
// biome-ignore-all lint/a11y/noAutofocus: shared component anatomy.
// biome-ignore-all lint/style/useTemplate: shared component anatomy.
// biome-ignore-all lint/complexity/useIndexOf: shared component anatomy.
// biome-ignore-all lint/security/noDangerouslySetInnerHtml: shared component anatomy.
// biome-ignore-all lint/suspicious/noArrayIndexKey: shared component anatomy.
// biome-ignore-all lint/a11y/useSemanticElements: shared component anatomy.
// biome-ignore-all lint/a11y/noInteractiveElementToNoninteractiveRole: shared component anatomy.
// biome-ignore-all lint/a11y/noStaticElementInteractions: shared component anatomy.
// biome-ignore-all lint/a11y/useKeyWithClickEvents: shared component anatomy.
// biome-ignore-all lint/a11y/noSvgWithoutTitle: shared component anatomy.
// biome-ignore-all lint/correctness/useExhaustiveDependencies: shared component anatomy.
export interface GitHubContributor {
  /** GitHub username. */
  login: string;
  /** Avatar image URL. */
  avatarUrl: string;
  /** Profile URL. */
  profileUrl: string;
  /** Number of contributions to the repository. */
  contributions: number;
}

/**
 * Fetch contributors for a GitHub repository.
 *
 * - Uses the public GitHub REST API — no API key required.
 * - Optionally authenticates with `process.env.GITHUB_TOKEN` to raise
 *   the rate limit from 60 → 5 000 requests per hour.
 * - Caches the result for 1 hour via Next.js ISR (`next.revalidate`).
 * - Returns up to `max` contributors sorted by contribution count (descending).
 *
 * Returns `null` if the request fails or the repo doesn't exist.
 */
export async function fetchContributors(
  owner: string,
  repo: string,
  max: number = 30,
): Promise<GitHubContributor[] | null> {
  try {
    const perPage = Math.min(max, 100);
    const response = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/contributors?per_page=${perPage}`,
      {
        headers: {
          Accept: "application/vnd.github.v3+json",
        },
      },
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (!Array.isArray(data)) return null;

    return data
      .filter(
        (c: Record<string, unknown>) =>
          typeof c.login === "string" && typeof c.contributions === "number",
      )
      .slice(0, max)
      .map((c: Record<string, unknown>) => ({
        login: c.login as string,
        avatarUrl: (c.avatar_url as string) ?? "",
        profileUrl: (c.html_url as string) ?? `https://github.com/${c.login}`,
        contributions: c.contributions as number,
      }));
  } catch {
    return null;
  }
}

/**
 * Format a contribution count for compact display.
 */
export function formatCount(count: number): string {
  if (count >= 1_000) {
    const value = count / 1_000;
    return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)}k`;
  }
  return count.toLocaleString("en-US");
}

export interface GitHubRepo {
  fullName: string;
  stars: number;
  forks: number;
  watchers: number;
  issues: number;
}

export interface GitHubRepoData extends GitHubRepo {
  description: string | null;
  language: string | null;
  languageColor: string | null;
  license: string | null;
  topics: string[];
  updatedAt: string | null;
  isArchived: boolean;
  isFork: boolean;
}

export type CIStatus =
  | "success"
  | "failure"
  | "pending"
  | "cancelled"
  | "skipped";

export interface CIStatusData {
  status: CIStatus;
  workflowName: string;
  branch: string;
  url: string;
  startedAt: string | null;
  durationSeconds: number | null;
}

export interface GitHubReleaseData {
  tag: string;
  name: string | null;
  url: string;
  publishedAt: string;
  preRelease: boolean;
  assetCount: number;
}

function getString(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function getNumber(value: unknown): number {
  return typeof value === "number" ? value : 0;
}

function mapRepo(data: Record<string, unknown>): GitHubRepoData {
  const license =
    data.license && typeof data.license === "object"
      ? (data.license as Record<string, unknown>)
      : null;
  return {
    fullName: getString(data.full_name) ?? "unknown/repo",
    stars: getNumber(data.stargazers_count),
    forks: getNumber(data.forks_count),
    watchers: getNumber(data.watchers_count ?? data.subscribers_count),
    issues: getNumber(data.open_issues_count),
    description: getString(data.description),
    language: getString(data.language),
    languageColor: null,
    license: getString(license?.spdx_id),
    topics: Array.isArray(data.topics)
      ? data.topics.filter(
          (topic): topic is string => typeof topic === "string",
        )
      : [],
    updatedAt: getString(data.updated_at),
    isArchived: data.archived === true,
    isFork: data.fork === true,
  };
}

export async function fetchGitHubRepo(
  owner: string,
  repo: string,
): Promise<GitHubRepo | null> {
  const data = await fetchGitHubRepoData(owner, repo);
  return data
    ? {
        fullName: data.fullName,
        stars: data.stars,
        forks: data.forks,
        watchers: data.watchers,
        issues: data.issues,
      }
    : null;
}

export async function fetchGitHubRepoData(
  owner: string,
  repo: string,
): Promise<GitHubRepoData | null> {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${owner}/${repo}`,
      {
        headers: { Accept: "application/vnd.github.v3+json" },
      },
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || typeof data !== "object") return null;
    return mapRepo(data as Record<string, unknown>);
  } catch {
    return null;
  }
}

export async function fetchLatestRelease(
  owner: string,
  repo: string,
): Promise<GitHubReleaseData | null> {
  try {
    const response = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/releases/latest`,
      {
        headers: { Accept: "application/vnd.github.v3+json" },
      },
    );
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || typeof data !== "object") return null;
    const row = data as Record<string, unknown>;
    const tag = getString(row.tag_name);
    const url = getString(row.html_url);
    const publishedAt =
      getString(row.published_at) ?? getString(row.created_at);
    if (!tag || !url || !publishedAt) return null;
    return {
      tag,
      name: getString(row.name),
      url,
      publishedAt,
      preRelease: row.prerelease === true,
      assetCount: Array.isArray(row.assets) ? row.assets.length : 0,
    };
  } catch {
    return null;
  }
}

export async function fetchCIStatus(
  owner: string,
  repo: string,
  workflow?: string,
  branch?: string,
): Promise<CIStatusData | null> {
  try {
    const params = new URLSearchParams({ per_page: "1" });
    if (branch) params.set("branch", branch);
    const workflowPath = workflow ? `workflows/${workflow}/runs` : "runs";
    const response = await fetch(
      `https://api.github.com/repos/${owner}/${repo}/actions/${workflowPath}?${params.toString()}`,
      { headers: { Accept: "application/vnd.github.v3+json" } },
    );
    if (!response.ok) return null;
    const data = await response.json();
    const run = Array.isArray(data.workflow_runs)
      ? (data.workflow_runs[0] as Record<string, unknown> | undefined)
      : undefined;
    if (!run) return null;
    const conclusion = getString(run.conclusion);
    const status = getString(run.status);
    const mapped: CIStatus =
      conclusion === "success"
        ? "success"
        : conclusion === "failure"
          ? "failure"
          : conclusion === "cancelled"
            ? "cancelled"
            : conclusion === "skipped"
              ? "skipped"
              : status === "completed"
                ? "failure"
                : "pending";
    const startedAt =
      getString(run.run_started_at) ?? getString(run.created_at);
    const updatedAt = getString(run.updated_at);
    const durationSeconds =
      startedAt && updatedAt
        ? Math.max(
            0,
            Math.round(
              (new Date(updatedAt).getTime() - new Date(startedAt).getTime()) /
                1000,
            ),
          )
        : null;
    return {
      status: mapped,
      workflowName: getString(run.name) ?? workflow ?? "CI",
      branch: getString(run.head_branch) ?? branch ?? "main",
      url:
        getString(run.html_url) ??
        `https://github.com/${owner}/${repo}/actions`,
      startedAt,
      durationSeconds,
    };
  } catch {
    return null;
  }
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  if (minutes < 60)
    return remaining ? `${minutes}m ${remaining}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

export function formatRelativeDate(iso: string): string {
  const date = new Date(iso);
  const diffDays = Math.floor((Date.now() - date.getTime()) / 86_400_000);
  if (diffDays <= 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  const months = Math.floor(diffDays / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}
